import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form, Input, Select, TextArea, Typography } from '@/components';
import { WindProjectTechnology } from '@/features/projects/types';
import { nullableStringSetter, getDateFromQuarterAndYear, getQuarterFromDate, getYearFromDate } from '@/helpers';
import { useDevelopmentStatus } from '@/hooks';
import { NUMERIC } from '@/features/projects/helpers/numeric-inputs';

export type WindFormFields = {
  development_status: string | null;
  installed_capacity: number | null;
  ready_to_build: string | null;
  ready_to_build_quarter: string | null;
  ready_to_build_year: number | null;
  commercial_operation_date: string | null;
  commercial_operation_date_quarter: string | null;
  commercial_operation_date_year: number | null;
  specific_production: number | null;
  comments: string | null;
  turbine_model: string | null;
  turbine_power: number | null;
  turbiness: number | null;
};

export type WindSubmitFields = {
  development_status: string | null;
  installed_capacity: number | null;
  specific_production: number | null;
  ready_to_build: string | null;
  commercial_operation_date: string | null;
  turbiness: number | null;
  turbine_model: string | null;
  turbine_power: number | null;
  comments: string | null;
};

export type WindFormProps = {
  readonly id: string;
  readonly item: WindProjectTechnology;
  readonly onSubmit: (data: WindSubmitFields) => void;
};

export function WindFormNew({ id, item, onSubmit }: WindFormProps) {
  const { t } = useTranslation(['projects']);
  const { options: developmentStatusOptions } = useDevelopmentStatus();

  const quarterOptions = [
    { label: 'Q1', value: '1' },
    { label: 'Q2', value: '2' },
    { label: 'Q3', value: '3' },
    { label: 'Q4', value: '4' },
  ];

  const options = useMemo(
    () => ({
      defaultValues: {
        development_status: item.development_status ?? null,
        installed_capacity: item.installed_capacity ?? null,
        ready_to_build: item.ready_to_build ?? null,
        ready_to_build_quarter: getQuarterFromDate(item.ready_to_build),
        ready_to_build_year: getYearFromDate(item.ready_to_build),
        commercial_operation_date_quarter: getQuarterFromDate(item.commercial_operation_date),
        commercial_operation_date_year: getYearFromDate(item.commercial_operation_date),
        commercial_operation_date: item.commercial_operation_date ?? null,
        specific_production: item.specific_production ?? null,
        comments: item.comments ?? null,
        turbine_model: item?.turbine_model ?? null,
        turbine_power: item?.turbine_power ?? null,
        turbiness: item?.turbiness ?? null,
      },
    }),
    [item]
  );

  const onSubmitWindForm = useCallback(
    (data: WindFormFields) => {
      data.ready_to_build = getDateFromQuarterAndYear(data.ready_to_build_quarter, data.ready_to_build_year);

      data.commercial_operation_date = getDateFromQuarterAndYear(
        data.commercial_operation_date_quarter,
        data.commercial_operation_date_year
      );

      const payload = {
        development_status: data.development_status,
        installed_capacity: data.installed_capacity,
        specific_production: data.specific_production,
        ready_to_build: data.ready_to_build,
        commercial_operation_date: data.commercial_operation_date,
        turbiness: data.turbiness,
        turbine_model: data.turbine_model,
        turbine_power: data.turbine_power,
        comments: data.comments,
      };

      onSubmit(payload);
    },
    [onSubmit]
  );

  return (
    <Form<WindFormFields> id={id} onSubmit={(data) => onSubmitWindForm(data)} options={options}>
      {({ formState, register }) => (
        <div className="space-y-6">
          <Typography as="p" color="secondary" size="body-3" weight="normal" className="mt-1 italic">
            {t(
              'Please fill in this section with the main details about the project, including technology, development status, location, installed capacity, ready-to-build (RtB) and COD dates, etc.'
            )}
          </Typography>
          <Field
            id="development_status"
            label={t('Development status')}
            error={formState.errors['development_status']}
            delay={0}
          >
            <Select
              id="development_status"
              registration={register('development_status', { setValueAs: nullableStringSetter })}
              options={developmentStatusOptions}
              placeholder={t('Select a development status')}
              color={formState.errors['development_status'] ? 'error' : 'primary'}
            />
          </Field>
          <Field
            id="installed_capacity"
            label={t('Installed Capacity')}
            error={formState.errors['installed_capacity']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="installed_capacity"
              type="number"
              suffix="MWp"
              placeholder={t('Example: 300')}
              color={formState.errors['installed_capacity'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('installed_capacity', { ...NUMERIC })}
            />
          </Field>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Field
              id="ready_to_build"
              label={t('Ready to Build Date')}
              error={formState.errors['ready_to_build']}
              delay={0.25}
              classes={{ field: 'space-y-1' }}
            >
              <div className="flex gap-1">
                <Select
                  id="ready_to_build_quarter"
                  options={quarterOptions}
                  placeholder={t('Select a quarter')}
                  color={formState.errors['ready_to_build_quarter'] ? 'error' : 'primary'}
                  registration={register('ready_to_build_quarter')}
                />
                <Input
                  id="ready_to_build_year"
                  type="number"
                  step="1"
                  color={formState.errors['ready_to_build_year'] ? 'error' : 'primary'}
                  autoComplete="off"
                  registration={register('ready_to_build_year', { ...NUMERIC })}
                  placeholder={t('Year')}
                />
              </div>
            </Field>

            <Field
              id="commercial_operation_date"
              label={t('Commercial Operation Date')}
              error={formState.errors['ready_to_build']}
              delay={0.25}
              classes={{ field: 'space-y-1' }}
            >
              <div className="flex gap-1">
                <Select
                  id="commercial_operation_date_quarter"
                  options={quarterOptions}
                  placeholder={t('Select a quarter')}
                  color={formState.errors['commercial_operation_date_quarter'] ? 'error' : 'primary'}
                  registration={register('commercial_operation_date_quarter')}
                />
                <Input
                  id="commercial_operation_date_year"
                  type="number"
                  step="1"
                  color={formState.errors['commercial_operation_date_year'] ? 'error' : 'primary'}
                  autoComplete="off"
                  registration={register('commercial_operation_date_year', { ...NUMERIC })}
                  placeholder={t('Year')}
                />
              </div>
            </Field>
          </div>

          <Field
            id="specific_production"
            label={t('Specific production')}
            error={formState.errors['specific_production']}
            delay={0.45}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="specific_production"
              type="number"
              suffix="MWh/MWp"
              placeholder={t('Example: 379')}
              color={formState.errors['specific_production'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('specific_production', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="turbine_model"
            label={t('Turbine model')}
            error={formState.errors['turbine_model']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="turbine_model"
              type="text"
              color={formState.errors['turbine_model'] ? 'error' : 'primary'}
              registration={register('turbine_model', { setValueAs: nullableStringSetter })}
            />
          </Field>

          <Field
            id="turbine_power"
            label={t('Turbine power')}
            error={formState.errors['turbine_power']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="turbine_power"
              type="number"
              suffix="MW"
              color={formState.errors['turbine_power'] ? 'error' : 'primary'}
              registration={register('turbine_power', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="turbiness"
            label={t('Number of turbines')}
            error={formState.errors['turbiness']}
            delay={0.25}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="turbiness"
              type="number"
              step="1"
              color={formState.errors['turbiness'] ? 'error' : 'primary'}
              registration={register('turbiness', { min: 0, ...NUMERIC })}
            />
          </Field>

          <Field
            id="comments"
            label={t('Comments')}
            error={formState.errors['comments']}
            delay={0.45}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="comments"
              color={formState.errors['comments'] ? 'error' : 'primary'}
              registration={register('comments', { setValueAs: nullableStringSetter })}
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
